import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Mic, Music, ChevronRight, Lock, Download, Loader2, Zap, Info,
  RotateCcw, Play, Eye, EyeOff, Check, ArrowUpRight, Volume2,
  Headphones, Film, Smartphone,
} from 'lucide-react';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SYNC_DRIFT = 0.3;
const PREVIEW_DURATION = 5000;
const VOICE_RMS_THRESHOLD = 15;
const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* ── Presets ─────────────────────────────────────────────── */
const PRESETS = {
  podcast:      { label: 'Podcast',  db: -18, atk: 80,  rel: 250, tip: 'Best for clear spoken content. Strong ducking (−18 dB).' },
  social_media: { label: 'Social',   db: -12, atk: 120, rel: 350, tip: 'Balanced voice clarity and music energy (−12 dB).' },
  cinematic:    { label: 'Cinematic', db: -6,  atk: 200, rel: 600, tip: 'Subtle ducking for dramatic effect (−6 dB).' },
};
const PRESET_ICONS = { podcast: Headphones, social_media: Smartphone, cinematic: Film };

/* ── LocalStorage helpers ────────────────────────────────── */
const gs  = (k, f) => { try { const v = localStorage.getItem(k); return v ?? f; } catch { return f; } };
const gn  = (k, f) => { try { const v = localStorage.getItem(k); return v !== null ? parseFloat(v) : f; } catch { return f; } };
const gb  = (k, f) => { try { const v = localStorage.getItem(k); return v !== null ? v === 'true' : f; } catch { return f; } };
const ss  = (k, v) => { try { localStorage.setItem(k, String(v)); } catch {} };

/* ═══════════════════════════════════════════════════════════
   VideoMixPlayer — main component
   ═══════════════════════════════════════════════════════════ */
export default function VideoMixPlayer({
  videoId, videoSrc, poster, token,
  hasStemsOverride = null, onExportMix,
  userTier = 'starter', isFounder = false,
}) {
  /* ── refs ── */
  const videoRef      = useRef(null);
  const voiceRef      = useRef(null);
  const musicRef      = useRef(null);
  const ctxRef        = useRef(null);   // AudioContext
  const analyserRef   = useRef(null);
  const musicGainRef  = useRef(null);
  const duckRafRef    = useRef(null);
  const audioInitRef  = useRef(false);
  const previewTmRef  = useRef(null);
  const stripRef      = useRef(null);   // canvas
  const historyRef    = useRef([]);     // [{t, a}]
  const voiceSeenRef  = useRef(false);

  /* ── state ── */
  const [playing, setPlaying]           = useState(false);
  const [stemsMeta, setStemsMeta]       = useState(hasStemsOverride);
  const [stemsLoading, setStemsLoading] = useState(hasStemsOverride === null);
  const [panelOpen, setPanelOpen]       = useState(false);
  const [exporting, setExporting]       = useState(false);

  // Re-render (legacy → stems)
  const [regenStatus, setRegenStatus] = useState(null); // null | 'queued' | 'running' | 'completed' | 'failed'
  const [regenProgress, setRegenProgress] = useState(0);
  const [regenMessage, setRegenMessage] = useState('');
  const regenPollRef = useRef(null);

  // Volumes
  const [masterVol, setMasterVol] = useState(() => gn('playback_volume_video', 0.8));
  const [voiceVol, setVoiceVol]   = useState(() => gn('voice_mix_level', 1.0));
  const [musicVol, setMusicVol]   = useState(() => gn('music_mix_level', 0.55));

  // Ducking
  const [duckOn, setDuckOn]           = useState(() => gb('ducking_enabled', true));
  const [preset, setPreset]           = useState(() => gs('ducking_preset', 'social_media'));
  const [enhanced, setEnhanced]       = useState(() => gs('before_after_mode', 'enhanced') === 'enhanced');
  const [duckActivity, setDuckActivity] = useState(0);

  // UX extras
  const [previewActive, setPreviewActive] = useState(false);
  const [overlay, setOverlay]             = useState(() => gb('ducking_overlay_enabled', false));
  const [exportDone, setExportDone]       = useState(null);     // null | {success}
  const [autoNote, setAutoNote]           = useState(false);

  const hasStemsReal = stemsMeta === true;
  const canExport    = isFounder || ['pro', 'elite'].includes(userTier?.toLowerCase());
  const duckingLive  = hasStemsReal && duckOn && enhanced;

  const voiceUrl = hasStemsReal && videoId && token
    ? `${BACKEND}/api/media/video/${videoId}/stem/voice?token=${encodeURIComponent(token)}` : null;
  const musicUrl = hasStemsReal && videoId && token
    ? `${BACKEND}/api/media/video/${videoId}/stem/music?token=${encodeURIComponent(token)}` : null;

  /* ─────────────── Fetch stems info ─────────────── */
  useEffect(() => {
    if (hasStemsOverride !== null || !videoId || !token) { setStemsLoading(false); return; }
    let off = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/media/video/${videoId}/stems-info`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!off && r.ok) { const d = await r.json(); setStemsMeta(d.has_stems); }
        else if (!off) setStemsMeta(false);
      } catch { if (!off) setStemsMeta(false); }
      finally { if (!off) setStemsLoading(false); }
    })();
    return () => { off = true; };
  }, [videoId, token, hasStemsOverride]);

  /* ─────────────── Init Web Audio graph ─────────────── */
  const initAudio = useCallback(() => {
    if (audioInitRef.current || !hasStemsReal) return;
    const ve = voiceRef.current, me = musicRef.current;
    if (!ve || !me) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const vs = ctx.createMediaElementSource(ve);
      const ms = ctx.createMediaElementSource(me);
      const an = ctx.createAnalyser();
      an.fftSize = 256; an.smoothingTimeConstant = 0.8;
      analyserRef.current = an;
      const mg = ctx.createGain();
      musicGainRef.current = mg;
      vs.connect(an); an.connect(ctx.destination);
      ms.connect(mg); mg.connect(ctx.destination);
      audioInitRef.current = true;
    } catch (e) { console.warn('WebAudio init:', e); }
  }, [hasStemsReal]);

  /* ─────────────── Ducking loop ─────────────── */
  useEffect(() => {
    if (!duckingLive || !playing) {
      if (duckRafRef.current) cancelAnimationFrame(duckRafRef.current);
      // Restore gain instantly when ducking stops
      if (musicGainRef.current && ctxRef.current) {
        musicGainRef.current.gain.cancelScheduledValues(ctxRef.current.currentTime);
        musicGainRef.current.gain.setValueAtTime(1.0, ctxRef.current.currentTime);
      }
      setDuckActivity(0);
      return;
    }
    const an = analyserRef.current, mg = musicGainRef.current, ctx = ctxRef.current;
    if (!an || !mg || !ctx) return;

    const p = PRESETS[preset] || PRESETS.social_media;
    const ratio = Math.pow(10, p.db / 20);
    const atkT = p.atk / 1000, relT = p.rel / 1000;
    const buf = new Uint8Array(an.frequencyBinCount);

    const tick = () => {
      an.getByteTimeDomainData(buf);
      let sq = 0;
      for (let i = 0; i < buf.length; i++) { const x = (buf[i] - 128) / 128; sq += x * x; }
      const rms = Math.sqrt(sq / buf.length) * 200;

      if (rms > VOICE_RMS_THRESHOLD) {
        mg.gain.setTargetAtTime(ratio, ctx.currentTime, atkT);
        setDuckActivity(Math.min(1, rms / 60));
        // Auto-detect voice → set smart default once
        if (!voiceSeenRef.current) {
          voiceSeenRef.current = true;
          setAutoNote(true);
          setTimeout(() => setAutoNote(false), 3500);
        }
      } else {
        mg.gain.setTargetAtTime(1.0, ctx.currentTime, relT);
        setDuckActivity(0);
      }

      // Collect overlay data
      if (overlay && videoRef.current) {
        const dur = videoRef.current.duration;
        if (dur > 0) {
          historyRef.current.push({ t: videoRef.current.currentTime / dur, a: rms > VOICE_RMS_THRESHOLD ? Math.min(1, rms / 60) : 0 });
          if (historyRef.current.length > 2000) historyRef.current = historyRef.current.slice(-1500);
        }
      }
      duckRafRef.current = requestAnimationFrame(tick);
    };
    duckRafRef.current = requestAnimationFrame(tick);
    return () => { if (duckRafRef.current) cancelAnimationFrame(duckRafRef.current); };
  }, [duckingLive, playing, preset, overlay, enhanced]);

  /* ─────────────── Before/After: instant gain reset ─────────────── */
  useEffect(() => {
    if (!enhanced && musicGainRef.current && ctxRef.current) {
      musicGainRef.current.gain.cancelScheduledValues(ctxRef.current.currentTime);
      musicGainRef.current.gain.setValueAtTime(1.0, ctxRef.current.currentTime);
    }
  }, [enhanced]);

  /* ─────────────── Volume application ─────────────── */
  useEffect(() => {
    if (hasStemsReal) {
      if (videoRef.current) { videoRef.current.muted = true; videoRef.current.volume = 0; }
      if (voiceRef.current) voiceRef.current.volume = Math.min(1, masterVol * voiceVol);
      if (musicRef.current) musicRef.current.volume = Math.min(1, masterVol * musicVol);
    } else {
      if (videoRef.current) { videoRef.current.muted = false; videoRef.current.volume = masterVol; }
    }
  }, [masterVol, voiceVol, musicVol, hasStemsReal]);

  /* ─────────────── Draw overlay strip ─────────────── */
  useEffect(() => {
    if (!overlay || !stripRef.current) return;
    const cvs = stripRef.current;
    const ctx2 = cvs.getContext('2d');
    const w = cvs.width, h = cvs.height;
    const id = setInterval(() => {
      ctx2.clearRect(0, 0, w, h);
      for (const s of historyRef.current) {
        const x = Math.floor(s.t * w);
        ctx2.fillStyle = `rgba(245,158,11,${s.a * 0.85})`;
        ctx2.fillRect(x, 0, Math.max(2, w / 300), h);
      }
    }, 250);
    return () => clearInterval(id);
  }, [overlay]);

  /* ─────────────── Sync ─────────────── */
  const syncAudio = useCallback(() => {
    const v = videoRef.current;
    if (!v || !hasStemsReal) return;
    const ct = v.currentTime;
    [voiceRef.current, musicRef.current].forEach(a => {
      if (a && Math.abs(a.currentTime - ct) > SYNC_DRIFT) a.currentTime = ct;
    });
  }, [hasStemsReal]);

  /* ─────────────── Play / Pause / Seek ─────────────── */
  const onPlay = useCallback(() => {
    setPlaying(true);
    if (ctxRef.current?.state === 'suspended') ctxRef.current.resume();
    if (hasStemsReal) {
      if (!audioInitRef.current) initAudio();
      voiceRef.current?.play().catch(() => {});
      musicRef.current?.play().catch(() => {});
    }
  }, [hasStemsReal, initAudio]);

  const onPause = useCallback(() => {
    setPlaying(false);
    if (hasStemsReal) { voiceRef.current?.pause(); musicRef.current?.pause(); }
  }, [hasStemsReal]);

  const onSeeked = useCallback(() => { if (hasStemsReal) syncAudio(); }, [hasStemsReal, syncAudio]);
  const onEnded = useCallback(() => {
    setPlaying(false);
    if (hasStemsReal) { voiceRef.current?.pause(); musicRef.current?.pause(); }
  }, [hasStemsReal]);

  /* ─────────────── Volume handlers ─────────────── */
  const setMaster = v => { const n = v[0] / 100; setMasterVol(n); ss('playback_volume_video', n); };
  const setVoice  = v => { const n = v[0] / 100; setVoiceVol(n);  ss('voice_mix_level', n); };
  const setMusic  = v => { const n = v[0] / 100; setMusicVol(n);  ss('music_mix_level', n); };

  /* ─────────────── Ducking control ─────────────── */
  const toggleDuck = on => { setDuckOn(on); ss('ducking_enabled', on); };
  const pickPreset = p  => { setPreset(p);  ss('ducking_preset', p); };
  const toggleMode = () => {
    const next = !enhanced;
    setEnhanced(next);
    ss('before_after_mode', next ? 'enhanced' : 'original');
  };
  const toggleOverlay = () => {
    const next = !overlay;
    setOverlay(next);
    ss('ducking_overlay_enabled', next);
    if (!next) { historyRef.current = []; } // clear history
  };
  const resetAll = () => {
    setVoiceVol(1.0); setMusicVol(0.55); setDuckOn(true); setPreset('social_media'); setEnhanced(true); setOverlay(false);
    ss('voice_mix_level', 1.0); ss('music_mix_level', 0.55); ss('ducking_enabled', true);
    ss('ducking_preset', 'social_media'); ss('before_after_mode', 'enhanced'); ss('ducking_overlay_enabled', false);
    historyRef.current = [];
  };

  /* ─────────────── Preview (5 s, no credits) ─────────────── */
  const startPreview = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (ctxRef.current?.state === 'suspended') ctxRef.current.resume();
    if (!audioInitRef.current && hasStemsReal) initAudio();

    const from = v.paused ? (v.currentTime || 0) : v.currentTime;
    v.currentTime = from;
    v.play().catch(() => {});
    if (hasStemsReal) {
      if (voiceRef.current) { voiceRef.current.currentTime = from; voiceRef.current.play().catch(() => {}); }
      if (musicRef.current) { musicRef.current.currentTime = from; musicRef.current.play().catch(() => {}); }
    }
    setPreviewActive(true);
    if (previewTmRef.current) clearTimeout(previewTmRef.current);
    previewTmRef.current = setTimeout(() => {
      videoRef.current?.pause();
      if (hasStemsReal) { voiceRef.current?.pause(); musicRef.current?.pause(); }
      setPreviewActive(false);
    }, PREVIEW_DURATION);
  }, [hasStemsReal, initAudio]);

  /* ─────────────── Export ─────────────── */
  const handleExport = async () => {
    if (!canExport || !videoId || !token || exporting) return;
    setExporting(true);
    const p = PRESETS[preset] || PRESETS.social_media;
    try {
      const r = await fetch(`${BACKEND}/api/reels/${videoId}/export-mix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          voice_gain: voiceVol, music_gain: musicVol,
          ducking_enabled: duckOn && enhanced,
          ducking_amount_db: p.db, ducking_attack_ms: p.atk, ducking_release_ms: p.rel,
          ducking_preset: preset,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setExportDone({ success: true, duckApplied: duckOn && enhanced, preset });
        setTimeout(() => setExportDone(null), 6000);
        if (onExportMix) onExportMix(d);
      } else {
        const err = await r.json().catch(() => ({}));
        setExportDone({ success: false, msg: err.detail || 'Export failed' });
        setTimeout(() => setExportDone(null), 5000);
      }
    } catch (e) {
      setExportDone({ success: false, msg: e.message });
      setTimeout(() => setExportDone(null), 5000);
    } finally { setExporting(false); }
  };

  /* ─────────────── Regenerate stems (legacy → stems) ─────────────── */
  const startRegeneration = useCallback(async () => {
    if (!videoId || !token) return;
    setRegenStatus('queued');
    setRegenProgress(0);
    setRegenMessage('Starting...');
    try {
      const r = await fetch(`${BACKEND}/api/reels/${videoId}/regenerate-stems`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.status === 403) {
        setRegenStatus('tier_blocked');
        setRegenMessage(d.detail || 'Requires Pro or higher');
        return;
      }
      if (d.status === 'already_has_stems') {
        setStemsMeta(true);
        setRegenStatus(null);
        return;
      }
      setRegenStatus(d.status);
      setRegenMessage(d.message);

      // Start polling
      if (regenPollRef.current) clearInterval(regenPollRef.current);
      regenPollRef.current = setInterval(async () => {
        try {
          const pr = await fetch(`${BACKEND}/api/reels/${videoId}/stems-job-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!pr.ok) return;
          const pd = await pr.json();
          setRegenProgress(pd.progress || 0);
          setRegenMessage(pd.message || '');
          setRegenStatus(pd.status);
          if (pd.status === 'completed') {
            clearInterval(regenPollRef.current);
            regenPollRef.current = null;
            setStemsMeta(true); // Switch to StemsMixPanel
          } else if (pd.status === 'failed') {
            clearInterval(regenPollRef.current);
            regenPollRef.current = null;
          }
        } catch {}
      }, 2000);
    } catch (e) {
      setRegenStatus('failed');
      setRegenMessage(e.message);
    }
  }, [videoId, token]);

  /* ─────────────── Cleanup ─────────────── */
  useEffect(() => () => {
    if (duckRafRef.current) cancelAnimationFrame(duckRafRef.current);
    if (previewTmRef.current) clearTimeout(previewTmRef.current);
    if (regenPollRef.current) clearInterval(regenPollRef.current);
    if (ctxRef.current?.state !== 'closed') ctxRef.current?.close().catch(() => {});
  }, []);

  /* ═══════════════════════════════ RENDER ═══════════════════════════════ */
  return (
    <div className="relative w-full h-full" data-testid="video-mix-player">
      {/* ── Video ── */}
      <video
        ref={videoRef} src={videoSrc} poster={poster} controls playsInline
        className="w-full h-full object-contain"
        onPlay={onPlay} onPause={onPause} onSeeked={onSeeked}
        onTimeUpdate={hasStemsReal ? syncAudio : undefined} onEnded={onEnded}
        onLoadedMetadata={() => {
          const v = videoRef.current; if (!v) return;
          if (hasStemsReal) { v.muted = true; v.volume = 0; } else v.volume = masterVol;
        }}
        data-testid="video-mix-video-element"
      />

      {/* ── Hidden stem audio ── */}
      {voiceUrl && <audio ref={voiceRef} src={voiceUrl} preload="auto" crossOrigin="anonymous" data-testid="voice-stem-audio" />}
      {musicUrl && <audio ref={musicRef} src={musicUrl} preload="auto" crossOrigin="anonymous" data-testid="music-stem-audio" />}

      {/* ── Ducking overlay strip ── */}
      {overlay && hasStemsReal && (
        <div className="absolute bottom-[42px] left-0 right-0 z-[5] pointer-events-none px-1" data-testid="ducking-strip-container">
          <canvas ref={stripRef} width={600} height={4} className="w-full h-1 rounded-full opacity-80" />
        </div>
      )}

      {/* ── Auto-voice note ── */}
      {autoNote && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 bg-amber-500/90 text-zinc-900 text-[11px] font-medium px-3 py-1.5 rounded-full animate-in fade-in zoom-in-95 duration-300" data-testid="auto-voice-note">
          Optimized for voice clarity
        </div>
      )}

      {/* ── Preview badge ── */}
      {previewActive && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-20 bg-zinc-900/80 text-amber-400 text-[10px] font-medium px-3 py-1 rounded-full border border-amber-500/30 animate-pulse" data-testid="preview-badge">
          Preview — no credits used
        </div>
      )}

      {/* ── Export summary overlay ── */}
      {exportDone && <ExportSummary data={exportDone} onClose={() => setExportDone(null)} />}

      {/* ── Mix panel ── */}
      <div className="absolute top-2 right-2 z-10 select-none" data-testid="mix-panel-container">
        {stemsLoading ? (
          <div className="bg-zinc-900/80 backdrop-blur-sm rounded-xl px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div>
        ) : hasStemsReal ? (
          <StemsMixPanel
            open={panelOpen} setOpen={setPanelOpen}
            masterVol={masterVol} voiceVol={voiceVol} musicVol={musicVol}
            setMaster={setMaster} setVoice={setVoice} setMusic={setMusic}
            duckOn={duckOn} toggleDuck={toggleDuck}
            preset={preset} pickPreset={pickPreset}
            enhanced={enhanced} toggleMode={toggleMode}
            duckActivity={duckActivity}
            overlay={overlay} toggleOverlay={toggleOverlay}
            canExport={canExport} exporting={exporting} onExport={handleExport}
            previewActive={previewActive} onPreview={startPreview}
            onReset={resetAll} userTier={userTier}
          />
        ) : (
          <LegacyPanel
            open={panelOpen} setOpen={setPanelOpen}
            masterVol={masterVol} setMaster={setMaster}
            canRerender={canExport}
            regenStatus={regenStatus} regenProgress={regenProgress}
            regenMessage={regenMessage} onRerender={startRegeneration}
            userTier={userTier}
          />
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   StemsMixPanel — full-featured panel with all 10 enhancements
   ══════════════════════════════════════════════════════════════ */
function StemsMixPanel({
  open, setOpen,
  masterVol, voiceVol, musicVol, setMaster, setVoice, setMusic,
  duckOn, toggleDuck, preset, pickPreset,
  enhanced, toggleMode, duckActivity,
  overlay, toggleOverlay,
  canExport, exporting, onExport,
  previewActive, onPreview,
  onReset, userTier,
}) {
  return (
    <div className="bg-zinc-900/90 backdrop-blur-md rounded-xl border border-zinc-800/50 shadow-2xl w-[230px]" data-testid="stems-mix-panel">
      {/* ── Header toggle ── */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs text-zinc-300 hover:text-white transition-colors w-full"
        data-testid="audio-mix-toggle"
      >
        <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        <Mic className="w-3 h-3 text-amber-400" />
        <span className="font-medium">Audio Mix</span>
        {duckOn && enhanced && (
          <Zap
            className="w-3 h-3 text-amber-400 ml-auto transition-opacity"
            style={{ opacity: 0.4 + duckActivity * 0.6 }}
          />
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200 text-[11px]" data-testid="audio-mix-panel-open">

          {/* ── 1. Before / After toggle ── */}
          <div className="flex items-center bg-zinc-800/60 rounded-lg p-0.5 gap-0.5" data-testid="before-after-toggle">
            <ModeBtn active={!enhanced} onClick={toggleMode} label="Original Mix" testId="mode-original" />
            <ModeBtn active={enhanced}  onClick={toggleMode} label="Enhanced Mix" testId="mode-enhanced" icon={<Zap className="w-3 h-3" />} />
          </div>

          {/* ── Section: Levels ── */}
          <SectionLabel text="Levels" />
          <MixSlider label="Master" value={masterVol} onChange={setMaster} testId="master-vol-slider" icon={<Volume2 className="w-3 h-3" />} />
          <MixSlider label="Voiceover" value={voiceVol} onChange={setVoice} testId="voice-mix-slider" icon={<Mic className="w-3 h-3" />} />
          <MixSlider label="Music" value={musicVol} onChange={setMusic} testId="music-mix-slider" icon={<Music className="w-3 h-3" />} />

          {/* ── Section: Smart Voice Clarity ── */}
          <div className="border-t border-zinc-800/50 pt-2 mt-1" data-testid="ducking-section">
            <div className="flex items-center justify-between mb-1">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-zinc-300 flex items-center gap-1 cursor-help font-medium text-[11px]">
                      <Zap className="w-3 h-3 text-amber-400" />
                      Smart Voice Clarity
                      <Info className="w-2.5 h-2.5 text-zinc-600" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[190px] text-[10px]">
                    Improves speech clarity by lowering music during narration
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Switch checked={duckOn} onCheckedChange={toggleDuck} className="scale-[0.7] origin-right" data-testid="ducking-toggle" />
            </div>

            {duckOn && enhanced && (
              <div className="space-y-1.5 animate-in fade-in duration-150">
                {/* 2. Live ducking activity visualizer */}
                <DuckingViz activity={duckActivity} />

                {/* 3. Preset buttons with tooltips */}
                <div className="flex gap-1" data-testid="ducking-presets">
                  {Object.entries(PRESETS).map(([k, p]) => {
                    const Icon = PRESET_ICONS[k];
                    return (
                      <TooltipProvider key={k} delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => pickPreset(k)}
                              className={`flex-1 flex items-center justify-center gap-0.5 py-1 rounded-md text-[9px] font-medium transition-all
                                ${preset === k
                                  ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/40'
                                  : 'bg-zinc-800/50 text-zinc-500 hover:text-zinc-300'}`}
                              data-testid={`preset-${k}`}
                            >
                              <Icon className="w-2.5 h-2.5" />
                              {p.label}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-[10px] max-w-[170px]">{p.tip}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>

                {/* 4. Preview Ducking button */}
                <button
                  onClick={onPreview}
                  disabled={previewActive}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all text-[10px] font-medium disabled:opacity-40 border border-amber-500/20"
                  data-testid="preview-ducking-btn"
                >
                  <Play className="w-3 h-3" />
                  {previewActive ? 'Previewing...' : 'Preview Ducking (5 s)'}
                </button>
                <p className="text-[9px] text-zinc-600 text-center">Preview only — no credits used</p>

                {/* 5. Overlay toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                    {overlay ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
                    Ducking overlay
                  </span>
                  <Switch checked={overlay} onCheckedChange={toggleOverlay} className="scale-[0.6] origin-right" data-testid="overlay-toggle" />
                </div>
              </div>
            )}

            {duckOn && !enhanced && (
              <p className="text-[9px] text-zinc-600 italic">Switch to Enhanced Mix to hear ducking</p>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between pt-1.5 border-t border-zinc-800/40">
            <button onClick={onReset} className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-0.5" data-testid="mix-reset-btn">
              <RotateCcw className="w-2.5 h-2.5" /> Reset
            </button>
            {/* 8. Tier CTA */}
            {canExport ? (
              <button
                onClick={onExport} disabled={exporting}
                className="text-[10px] text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1 disabled:opacity-40 font-medium"
                data-testid="export-mix-btn"
              >
                {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {exporting ? 'Exporting...' : 'Export mix'}
              </button>
            ) : (
              <TierCTA tier={userTier} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── 2. Ducking Activity Visualizer ── */
function DuckingViz({ activity }) {
  const isActive = activity > 0.08;
  return (
    <div className="space-y-0.5" data-testid="ducking-viz">
      <div className="flex items-center gap-1.5">
        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden relative">
          <div
            className={`h-full bg-gradient-to-r from-amber-500 to-amber-300 rounded-full transition-all duration-150 ${isActive && !REDUCED_MOTION ? 'animate-pulse' : ''}`}
            style={{ width: `${Math.max(2, activity * 100)}%` }}
            data-testid="ducking-activity-bar"
          />
        </div>
        <span className="text-[9px] tabular-nums w-8 text-right" style={{ color: isActive ? '#fbbf24' : '#71717a' }}>
          {isActive ? 'Active' : 'Idle'}
        </span>
      </div>
      {isActive && (
        <p className="text-[9px] text-amber-400/70 animate-in fade-in duration-200" data-testid="ducking-label">
          Music lowering for voice clarity
        </p>
      )}
    </div>
  );
}

/* ── 7. Export Summary ── */
function ExportSummary({ data, onClose }) {
  if (!data) return null;
  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300 cursor-pointer"
      onClick={onClose} data-testid="export-summary"
    >
      {data.success ? (
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-5 max-w-[260px] text-center shadow-2xl space-y-3 animate-in zoom-in-95 duration-300">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-5 h-5 text-emerald-400" />
          </div>
          <p className="text-sm font-semibold text-zinc-100">Mix exported</p>
          <div className="space-y-1.5 text-left">
            <SummaryRow text="Voice clarity optimized" />
            <SummaryRow text="Music balanced automatically" />
            <SummaryRow text="Social-ready audio levels applied" />
            {data.duckApplied && <SummaryRow text={`${PRESETS[data.preset]?.label || 'Smart'} ducking applied`} />}
          </div>
          <p className="text-[10px] text-zinc-500">Tap anywhere to dismiss</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-red-800/40 rounded-2xl p-5 max-w-[240px] text-center shadow-2xl space-y-2">
          <p className="text-sm font-semibold text-red-400">Export failed</p>
          <p className="text-[11px] text-zinc-400">{data.msg}</p>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ text }) {
  return (
    <div className="flex items-center gap-2">
      <Check className="w-3 h-3 text-emerald-400 shrink-0" />
      <span className="text-[11px] text-zinc-300">{text}</span>
    </div>
  );
}

/* ── 8. Tier CTA ── */
function TierCTA({ tier }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-[9px] text-zinc-500 flex items-center gap-0.5 cursor-help" data-testid="tier-cta">
            <Lock className="w-2.5 h-2.5" /> Pro+ to export
            <ArrowUpRight className="w-2.5 h-2.5 text-amber-500" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-[10px] max-w-[180px]">
          Export with ducking available in Pro. Upgrade to unlock full audio mixing exports.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ── Before / After mode button ── */
function ModeBtn({ active, onClick, label, testId, icon }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-md text-[10px] font-medium transition-all
        ${active ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
      data-testid={testId}
    >
      {icon}{label}
    </button>
  );
}

/* ── Section label ── */
function SectionLabel({ text }) {
  return <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold pt-1">{text}</p>;
}

/* ── Legacy Panel ── */
function LegacyPanel({ open, setOpen, masterVol, setMaster, canRerender, regenStatus, regenProgress, regenMessage, onRerender, userTier }) {
  const isProcessing = regenStatus === 'queued' || regenStatus === 'running';
  return (
    <div className="bg-zinc-900/90 backdrop-blur-md rounded-xl border border-zinc-800/50 shadow-2xl w-[230px]" data-testid="legacy-volume-panel">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs text-zinc-300 hover:text-white transition-colors w-full"
        data-testid="legacy-volume-toggle"
      >
        <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        <Volume2 className="w-3 h-3" />
        Volume
        {isProcessing && <Loader2 className="w-3 h-3 ml-auto animate-spin text-amber-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
          <MixSlider label="Volume" value={masterVol} onChange={setMaster} testId="master-vol-slider" icon={<Volume2 className="w-3 h-3" />} />

          {/* Re-render button / status */}
          <div className="border-t border-zinc-800/50 pt-2" data-testid="rerender-section">
            {regenStatus === 'completed' ? (
              <div className="flex items-center gap-1.5 text-[10px] text-emerald-400 bg-emerald-500/10 rounded-lg px-2 py-1.5">
                <Check className="w-3 h-3 shrink-0" />
                <span>Stems ready! Reload to use Audio Mix.</span>
              </div>
            ) : isProcessing ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
                  <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  <span>{regenMessage || 'Processing...'}</span>
                </div>
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full transition-all duration-500"
                    style={{ width: `${regenProgress}%` }}
                    data-testid="regen-progress-bar"
                  />
                </div>
                <p className="text-[9px] text-zinc-600 text-center">{regenProgress}% complete</p>
              </div>
            ) : regenStatus === 'failed' ? (
              <div className="space-y-1.5">
                <p className="text-[10px] text-red-400">{regenMessage || 'Re-render failed'}</p>
                <button
                  onClick={onRerender}
                  className="w-full text-[10px] text-amber-400 hover:text-amber-300 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 transition-colors"
                  data-testid="retry-rerender-btn"
                >
                  Try again
                </button>
              </div>
            ) : regenStatus === 'tier_blocked' ? (
              <div className="space-y-1">
                <p className="text-[10px] text-zinc-400">{regenMessage}</p>
                <div className="flex items-center gap-1 text-[9px] text-amber-500">
                  <ArrowUpRight className="w-2.5 h-2.5" />
                  <span>Upgrade to Pro to unlock</span>
                </div>
              </div>
            ) : canRerender ? (
              <div className="space-y-1.5">
                <button
                  onClick={onRerender}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gradient-to-r from-amber-500/15 to-amber-600/10 text-amber-400 hover:from-amber-500/25 hover:to-amber-600/20 transition-all text-[11px] font-medium border border-amber-500/20"
                  data-testid="enable-mix-rerender-btn"
                >
                  <Zap className="w-3.5 h-3.5" />
                  Enable Voice/Music Mix
                </button>
                <p className="text-[9px] text-zinc-600 text-center">Re-render to unlock separate voice + music controls</p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-start gap-1.5 text-[10px] text-zinc-500 bg-zinc-800/40 rounded-lg px-2 py-1.5">
                  <Lock className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>Voice/Music mix &amp; Smart Clarity available for new renders</span>
                </div>
                <div className="flex items-center gap-1 text-[9px] text-amber-500 px-2">
                  <ArrowUpRight className="w-2.5 h-2.5" />
                  <span>Upgrade to Pro to re-render with mix controls</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Slider row ── */
function MixSlider({ label, icon, value, onChange, testId }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-zinc-400 flex items-center gap-1">{icon}{label}</span>
        <span className="text-[10px] text-zinc-500 tabular-nums w-7 text-right">{Math.round(value * 100)}%</span>
      </div>
      <Slider value={[Math.round(value * 100)]} onValueChange={onChange} max={100} step={1} className="w-full" data-testid={testId} />
    </div>
  );
}
